import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useMemo, useState } from 'react';

import type { IPersonsListParams, Segment } from '@feathr/blackbox';
import { DateTimeCell, Spinner, Time, Tooltip } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { TimeFormat } from '@feathr/hooks';

interface IProps {
  segment: Segment;
}

/*
 * A pixel can only have been seen in the past, so a future timestamp means bad data and the cell
 * falls back to "Never" rather than rendering "in 8 years".
 */
function validateLastSeen(lastSeen: string): string | undefined {
  const lastSeenDate = new Date(lastSeen);
  const now = new Date();

  if (!Number.isNaN(lastSeenDate.getTime()) && lastSeenDate < now) {
    return lastSeen;
  }
  return undefined;
}

/*
 * The last-seen date is not an attribute of the pixel: it is the most recent matched activity
 * across the people the segment resolves to, so the cell runs its own one-row Persons query
 * against the segment's predicates rather than reading the model.
 */
function ConversionPixelLastSeenCell({ segment }: Readonly<IProps>): JSX.Element {
  const [lastSeen, setLastSeen] = useState<string | undefined>();
  const [isPending, setIsPending] = useState(false);
  const { Persons } = useStore();

  const mode = segment.get('mode');
  const lookbackMode = segment.get('lookback_mode');
  const lookbackValue = segment.get('lookback_value');
  const predicates = useMemo(() => segment.get('predicates'), [segment]);

  useEffect(() => {
    /*
     * The row can collapse, or the pixel's attributes can change, while the query is in flight.
     * Without this an earlier run can resolve last and overwrite a newer result, and the disposer
     * stops the reaction outliving the cell.
     */
    let isCurrent = true;
    let disposeWhen: (() => void) | undefined;

    async function getLastSeen(): Promise<void> {
      setIsPending(true);
      const params = {
        mode,
        lookback_mode: lookbackMode,
        lookback_value: lookbackValue,
        predicates,
        include_date_last_matched_activity: true,
        pagination: {
          page_size: 1,
        },
        ordering: ['-date_last_matched_activity'],
      } as Partial<IPersonsListParams>;

      const persons = Persons.list(params, { url: Persons.url('page') });
      const settled = when(() => !persons.isPending);
      disposeWhen = settled.cancel;
      try {
        await settled;
      } catch {
        // Cancelling a when() rejects it; that only happens from the cleanup below.
        return;
      }

      if (!isCurrent) {
        return;
      }

      /*
       * Length-checked rather than indexed directly: reading index 0 of an empty MobX observable
       * array logs an out-of-bounds warning, and a pixel with no matched activity is the norm.
       */
      const matched = persons.models.length
        ? persons.models[0].get('date_last_matched_activity') ?? undefined
        : undefined;

      setLastSeen(matched ? validateLastSeen(matched) : undefined);
      setIsPending(false);
    }

    void getLastSeen();

    return (): void => {
      isCurrent = false;
      disposeWhen?.();
    };
  }, [mode, lookbackMode, lookbackValue, predicates, Persons]);

  if (isPending) {
    return <Spinner size={16} />;
  }

  if (!lastSeen) {
    return <DateTimeCell />;
  }

  return (
    <Tooltip title={<Time format={TimeFormat.longDate} timestamp={lastSeen} />}>
      <DateTimeCell format={TimeFormat.timeFromNow} value={lastSeen} />
    </Tooltip>
  );
}

export default observer(ConversionPixelLastSeenCell);
